import { MongoReservation } from '../types/index';
import { loadStripe } from '@stripe/stripe-js';
import {
    Stripe,
    StripeElements
} from '@stripe/stripe-js';
import api from './api';

// Charger l'instance Stripe avec la clé publique
export const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

interface PaymentIntentResponse {
    clientSecret: string;
    amount?: number;
}

export interface StripePaymentResult {
    success: boolean;
    error?: string;
    paymentIntentId?: string;
}

// Fonction pour créer une intention de paiement Stripe
export const createPaymentIntent = async (
    reservationData: Omit<MongoReservation, '_id'> | null,
    productId: string
): Promise<PaymentIntentResponse> => {
    if (!productId) {
        throw new Error("ID produit invalide");
    }

    const response = await api.post('/payment/create-payment-intent', {
        productId,
        quantity: reservationData?.participants || 1,
        currency: 'eur',
        description: `Réservation #${new Date().getTime()} - ${reservationData?.customer?.firstName || 'Client'} ${reservationData?.customer?.lastName || ''}`,
        reservationData,
    });

    return response.data;
};

// Fonction pour confirmer le paiement avec Stripe
export const confirmCardPayment = async (
    stripe: Stripe,
    elements: StripeElements,
    clientSecret: string,
    reservationData: Omit<MongoReservation, '_id'>
): Promise<StripePaymentResult> => {
    if (!stripe || !elements) {
        return { success: false, error: 'Stripe n\'est pas initialisé' };
    }

    const cardElement = elements.getElement('card');
    if (!cardElement) {
        return { success: false, error: 'Élément de carte non trouvé' };
    }

    try {
        const result = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
                card: cardElement,
                billing_details: {
                    name: `${reservationData.customer.firstName} ${reservationData.customer.lastName}`,
                    email: reservationData.customer.email,
                    phone: reservationData.customer.phone,
                },
            },
        });

        if (result.error) {
            return {
                success: false,
                error: result.error.message || 'Une erreur est survenue lors du paiement'
            };
        }

        if (result.paymentIntent?.status === 'succeeded') {
            return {
                success: true,
                paymentIntentId: result.paymentIntent.id
            };
        } else {
            return {
                success: false,
                error: `Statut du paiement: ${result.paymentIntent?.status || 'inconnu'}`
            };
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Une erreur inconnue est survenue';
        return { success: false, error: errorMessage };
    }
};

// Fonction pour finaliser la réservation après un paiement réussi
export const finalizeReservation = async (
    reservationData: Omit<MongoReservation, '_id'>,
    paymentIntentId: string
): Promise<any> => {
    const response = await api.post('/reservations/finalize', {
        reservationData,
        paymentIntentId
    });

    return response.data;
};

export const calculateTotalAmount = (cart: any[]): number => {
    if (!cart || !Array.isArray(cart) || cart.length === 0) {
        return 0;
    }

    // Calcul en centimes (montant total × 100)
    return Math.round(cart.reduce((total, item) => total + (item?.price || 0) * (item?.quantity || 1), 0) * 100);
};

export const calculateBookingAmount = (numberOfPeople: number): number => {
    // Prix de base par personne (en centimes)
    const BASE_PRICE = 8000; // 80€
    return (numberOfPeople || 1) * BASE_PRICE; // Valeur par défaut de 1 si nombre invalide
};